import type { IntlShape } from 'react-intl'
import { getCheckInCheckOutText } from 'components/Search/utils'
import { DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isItemCancelled } from 'lib/order/orderUtils'
import moment from 'moment'
import { formatFlightRouteLabel } from './flightDisplayUtils'
import { sumOccupancies } from './bookingOccupancyUtils'
import { formatOrderTileOccupancyLabel } from './orderTileDisplayUtils'

export interface OrderTypeSummary {
  typeId: string
  type: string
  count: number
  primaryName?: string
}

export interface OrderTileData {
  bookingType: 'order'
  itemId: string
  orderId: string
  bookingItemIds: Array<string>
  propertyName: string
  additionalBookings: number
  orderTypeIds: Array<string>
  checkInDate?: string
  imageId?: string
  checkOutDate?: string
  occupancy?: App.Occupants
  primaryFlightItemId?: string
  flightDepartureAirport?: string
  flightArrivalAirport?: string
  flightDepartureDate?: string
}

interface BookingTileSource {
  itemId: string
  bookingType: 'hotel' | 'experience' | 'flight'
  propertyName: string
  checkInDate?: string
  checkOutDate?: string
  imageId?: string
  occupancy?: App.Occupants
  flightItemId?: string
  flightDepartureAirport?: string
  flightArrivalAirport?: string
  flightDepartureDate?: string
}

export function getSelectedBookingItemIds({
  modalAddedBookingItemIds,
  bookingDataByTag,
  selectedTagSet,
}: {
  modalAddedBookingItemIds: Array<string>
  bookingDataByTag: Map<string, OrderTileData>
  selectedTagSet: ReadonlySet<string>
}): Array<string> {
  const itemIds = new Set(modalAddedBookingItemIds)

  for (const [tag, tile] of bookingDataByTag.entries()) {
    if (selectedTagSet.has(tag)) {
      tile.bookingItemIds.forEach(itemId => itemIds.add(itemId))
    }
  }

  return [...itemIds]
}

// Selection tag for a flight booking. Derived from stable order-item data
// (airports + departure date) rather than the asynchronously fetched flight
// details, so the tag never changes as ChatLandingFlightOrderTile hydrates.
// A tag that shifted after selection would orphan the entry in selectedTags
// and silently drop the booking from the trip.
export function buildFlightBookingTag(
  intl: IntlShape,
  tile: Pick<OrderTileData, 'flightDepartureAirport' | 'flightArrivalAirport' | 'flightDepartureDate'>,
): string {
  const parts: Array<string> = [
    formatFlightRouteLabel(intl, tile.flightDepartureAirport, tile.flightArrivalAirport),
  ]

  if (tile.flightDepartureDate) {
    parts.push(moment(tile.flightDepartureDate).format(DMY_CASUAL_SHORT_FORMAT))
  }

  return parts.join(' · ')
}

export function getAdditionalBookings(orderTypes: Array<OrderTypeSummary>): number {
  const bookings = orderTypes
    .filter(item => item.typeId !== 'insurance' && item.typeId !== 'luxPlus')
    .reduce((total, item) => total + item.count, 0)

  return Math.max(0, bookings - 1)
}

export function getOrderTypeIds(orderTypes: Array<OrderTypeSummary>): Array<string> {
  const ignoredTypeIds = new Set(['insurance', 'luxPlus'])
  return [...new Set(
    orderTypes
      .filter(({ typeId }) => !ignoredTypeIds.has(typeId))
      .map(({ typeId }) => typeId),
  )]
}

export function getOrderTileTitle(
  intl: IntlShape,
  tile: Pick<OrderTileData, 'propertyName' | 'additionalBookings'>,
): string {
  if (tile.additionalBookings === 0) {
    return tile.propertyName
  }

  const additionalBookingsText = intl.formatMessage(
    {
      id: '4diYptumZS6tDKtvhJSB',
      defaultMessage: '+ {count} more',
      description: 'Label showing additional bookings count in order title',
    },
    { count: tile.additionalBookings },
  )

  return `${tile.propertyName} ${additionalBookingsText}`
}

export function buildBookingTag({
  intl,
  tile,
}: {
  intl: IntlShape
  tile: OrderTileData
}): string {
  if (tile.primaryFlightItemId) {
    return buildFlightBookingTag(intl, tile)
  }

  const parts: Array<string> = [getOrderTileTitle(intl, tile)]

  if (tile.checkInDate && tile.checkOutDate) {
    parts.push(getCheckInCheckOutText(tile.checkInDate, tile.checkOutDate))
  } else if (tile.checkInDate) {
    parts.push(moment(tile.checkInDate).format(DMY_CASUAL_SHORT_FORMAT))
  }

  const occupancyLabel = formatOrderTileOccupancyLabel(intl, tile.occupancy)
  if (occupancyLabel) {
    parts.push(occupancyLabel)
  }

  return parts.join(' · ')
}

export function getOrderTileDataList(
  order: App.Order,
  orderTypes: Array<OrderTypeSummary> = [],
  orderTravelDuration?: { earliest?: string, latest?: string },
): Array<OrderTileData> {
  const sourceItems: Array<BookingTileSource> = []

  for (const item of order.items) {
    if (isItemCancelled(item) || !item.reservation?.startDate || !item.offer?.property?.name) continue
    sourceItems.push({
      bookingType: 'hotel',
      itemId: item.id,
      propertyName: item.offer.property.name,
      checkInDate: item.reservation.startDate,
      imageId: item.offer.property?.roomType?.images?.[0]?.id,
      checkOutDate: item.reservation.endDate,
      occupancy: { adults: item.reservation.adults, children: item.reservation.children, infants: item.reservation.infants },
    })
  }

  for (const item of order.bedbankItems) {
    if (isItemCancelled(item) || !item.offer?.property?.name) continue
    sourceItems.push({
      bookingType: 'hotel',
      itemId: item.id,
      propertyName: item.offer.property.name,
      checkInDate: item.checkIn.format(ISO_DATE_FORMAT),
      imageId: item.offer.image?.id,
      checkOutDate: item.checkOut.format(ISO_DATE_FORMAT),
      occupancy: sumOccupancies(item.rooms.map(room => room.occupancy)),
    })
  }

  for (const item of order.experienceItems) {
    if (isItemCancelled(item) || !item.title) continue
    sourceItems.push({
      bookingType: 'experience',
      itemId: item.id,
      propertyName: item.title,
      checkInDate: item.date,
    })
  }

  if (order.hasFlight) {
    for (const item of order.flightItems) {
      if (isItemCancelled(item)) continue
      sourceItems.push({
        bookingType: 'flight',
        itemId: item.id,
        propertyName: 'Flight booking',
        flightItemId: item.id,
        flightDepartureAirport: item.departingAirport,
        flightArrivalAirport: item.arrivalAirport,
        flightDepartureDate: item.departureDate,
      })
    }
  }

  const primary = sourceItems[0]
  if (!primary) return []

  const firstType = orderTypes[0]
  const propertyName = firstType?.primaryName || primary.propertyName

  return [
    {
      bookingType: 'order',
      itemId: order.id,
      orderId: order.id,
      bookingItemIds: [...new Set(sourceItems.map(item => item.itemId))],
      propertyName,
      additionalBookings: getAdditionalBookings(orderTypes),
      orderTypeIds: getOrderTypeIds(orderTypes),
      checkInDate: orderTravelDuration?.earliest ?? primary.checkInDate,
      checkOutDate: orderTravelDuration?.latest ?? primary.checkOutDate,
      imageId: primary.imageId,
      occupancy: primary.occupancy,
      primaryFlightItemId: primary.flightItemId,
      flightDepartureAirport: primary.flightDepartureAirport,
      flightArrivalAirport: primary.flightArrivalAirport,
      flightDepartureDate: primary.flightDepartureDate,
    },
  ]
}
